import { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import Table from '../components/ui/Table';
import Toast from '../components/ui/Toast';
import { fetchWithAdminAuth } from '../utils/adminAuth';

interface ActivityLog {
  id: string;
  adminId: string | null;
  adminName: string;
  action: string;
  details: string | null;
  ipAddress: string | null;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Suporte() {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  useEffect(() => {
    fetchLogs();
  }, [pagination.page, startDate, endDate, actionFilter]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      
      let url = `/api/admin/activity-logs?page=${pagination.page}&limit=${pagination.limit}`;
      
      if (startDate || endDate) {
        url = `/api/admin/activity-logs/date-range?page=${pagination.page}&limit=${pagination.limit}`;
        if (startDate) url += `&startDate=${startDate}`;
        if (endDate) url += `&endDate=${endDate}`;
      } else if (actionFilter) {
        url = `/api/admin/activity-logs/action/${encodeURIComponent(actionFilter)}?page=${pagination.page}&limit=${pagination.limit}`;
      }
      
      const response = await fetchWithAdminAuth(url);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      setToast({ message: 'Erro ao carregar logs', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!confirm('Tem certeza que deseja eliminar logs com mais de 90 dias?')) return;
    
    try {
      const response = await fetchWithAdminAuth('/api/admin/activity-logs/cleanup?days=90', {
        method: 'DELETE'
      });
      
      if (response.ok) {
        const data = await response.json();
        setToast({ message: `${data.deletedCount} logs eliminados`, type: 'success' });
        fetchLogs();
      }
    } catch (error) {
      setToast({ message: 'Erro ao limpar logs', type: 'error' });
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const columns = [
    { header: 'Data/hora', key: 'createdAt', render: (val: string) => formatDate(val) },
    { header: 'Utilizador', key: 'adminName' },
    { header: 'Ação', key: 'action' },
    { header: 'Detalhes', key: 'details', render: (val: string | null) => val || '-' }
  ];

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onClose={() => setToast(null)}
        />
      )}

      <Card
        title="Suporte & Logs"
        subtitle="Registo de atividades importantes e pedidos internos de suporte."
        action={
          <Button onClick={handleCleanup}>
            Limpar logs antigos
          </Button>
        }
      >
        {/* Filters */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">Data início</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            />
          </div>
          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">Data fim</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            />
          </div>
          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">Pesquisar ação</label>
            <input
              type="text"
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              placeholder="Pesquisar por ação..."
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            />
          </div>
        </div>

        <div className="mb-3 text-[0.75rem] text-text-soft">
          A mostrar {logs.length} de {pagination.total} registos
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            <Table columns={columns} data={logs} />
            
            {logs.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                Nenhum log encontrado
              </div>
            )}

            {/* Pagination */}
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              pageSize={pagination.limit}
              itemsOnPage={logs.length}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          </>
        )}
      </Card>
    </div>
  );
}
